import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  Box,
  Stack,
  useTheme,
  Text,
  DarkTooltip,
  Avatar,
} from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { useAccount, useSession } from '@ltvco/refresh-lib/v1';
import {
  Add as AddIcon,
  InsertDriveFile as InsertDriveFileIcon,
  KeyboardTab,
  KeyboardBackspace,
} from '@mui/icons-material';
import { useContext, useState, useRef } from 'react';

import {
  NewChatButton,
  SidebarIcon,
  HelpModal,
  ReportUsageCard,
  ProfileModal,
} from 'components/ChatBV/components';
import { BrandLogoChat } from 'components/ChatBV/components/BrandLogoChat';
import {
  CHATBV_GA4_EVENTS,
  FEEDBACK_FORM_URL,
} from 'components/ChatBV/constants';
import { ChatContext } from 'components/ChatBV/context/ChatContext';
import { useLimitedPlanInfo } from 'utils/useLimitedPlanInfo';

import { ProfileSection } from '../../Sidebar/ProfileSection';

interface ChatPageSidebarProps {
  onRecentReportsClick?: () => void;
  onCloseRecentReports?: () => void;
  onUpgradePlanClick?: () => void;
}

export const ChatPageSidebar = ({
  onRecentReportsClick,
  onCloseRecentReports,
  onUpgradePlanClick,
}: ChatPageSidebarProps) => {
  const theme = useTheme();
  const [openHelpModal, setOpenHelpModal] = useState(false);
  const [openProfileModal, setOpenProfileModal] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [isNewChatHovered, setIsNewChatHovered] = useState(false);
  const [isReportLibraryHovered, setIsReportLibraryHovered] = useState(false);
  const [isProfileHovered, setIsProfileHovered] = useState(false);
  const { handleNewChat } = useContext(ChatContext);
  const { trackEventGA4 } = useContext(AppConfig);
  const { isDesktop } = useScreenSize();
  const { reportsRun, reportLimit, reportsRunPercentage, isLimitedUser } =
    useLimitedPlanInfo();
  const {
    session: { isAuthenticated },
  } = useSession();
  const { data: account } = useAccount(isAuthenticated);
  const profileRef = useRef<HTMLDivElement>(null);

  const handleNewChatClick = () => {
    handleNewChat();
    onCloseRecentReports?.();
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: CHATBV_GA4_EVENTS.navSidebar.subTypes.new_chat,
    });
  };

  const handleToggleCollapsed = (value: boolean) => {
    if (isDesktop && value) {
      setCollapsed(value);
    } else {
      setCollapsed(false);
    }

    setCollapsed(value);
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: collapsed
        ? CHATBV_GA4_EVENTS.navSidebar.subTypes.expand
        : CHATBV_GA4_EVENTS.navSidebar.subTypes.collapse,
    });
  };

  const handleHelpModalClick = () => {
    setOpenHelpModal(true);
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: CHATBV_GA4_EVENTS.navSidebar.subTypes.help_modal,
    });
  };

  const handleFeedbackFormClick = () => {
    window.open(FEEDBACK_FORM_URL, '_blank');
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: CHATBV_GA4_EVENTS.navSidebar.subTypes.feedback_form,
    });
  };

  const handleRecentReportsClick = () => {
    onRecentReportsClick?.();
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: 'report_library',
    });
  };

  const handleUpgradePlanClick = () => {
    onUpgradePlanClick?.();
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: CHATBV_GA4_EVENTS.navSidebar.subTypes.upgrade_plan,
    });
  };

  const handleProfileClick = () => {
    setOpenProfileModal(true);
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: 'profile_modal',
    });
  };

  const getInitial = () => {
    const firstName = account?.account?.user_info?.first_name;
    const lastName = account?.account?.user_info?.last_name;
    const email = account?.account?.user_info?.email;

    if (firstName) {
      return firstName[0].toUpperCase();
    }
    if (lastName) {
      return lastName[0].toUpperCase();
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return 'U';
  };
  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
        }}
      >
        <Stack
          height="100%"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            p: 2,
          }}
        >
          <Stack spacing={4} paddingTop={6} alignItems="center">
            <DarkTooltip title={collapsed ? 'Expand' : ''}>
              <SidebarIcon>
                {collapsed ? (
                  <KeyboardTab onClick={() => handleToggleCollapsed(false)} />
                ) : (
                  <KeyboardBackspace
                    onClick={() => handleToggleCollapsed(true)}
                  />
                )}
              </SidebarIcon>
            </DarkTooltip>
            <DarkTooltip title={collapsed ? 'New Chat' : ''}>
              <NewChatButton
                variant="contained"
                onClick={handleNewChatClick}
                onMouseEnter={() => setIsNewChatHovered(true)}
                onMouseLeave={() => setIsNewChatHovered(false)}
                sx={{
                  backgroundColor: isNewChatHovered
                    ? theme.palette.button.dark
                    : theme.palette.primary.main,
                }}
              >
                <AddIcon
                  sx={{
                    width: '20px',
                    height: '20px',
                    color: theme.palette.text.contrastText,
                  }}
                />
              </NewChatButton>
            </DarkTooltip>
            <DarkTooltip title={collapsed ? 'Report Library' : ''}>
              <SidebarIcon
                onClick={handleRecentReportsClick}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    color: theme.palette.primary.main,
                  },
                }}
                onMouseEnter={() => setIsReportLibraryHovered(true)}
                onMouseLeave={() => setIsReportLibraryHovered(false)}
              >
                <InsertDriveFileIcon
                  sx={{
                    width: '20px',
                    height: '20px',
                    color: isReportLibraryHovered
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                  }}
                />
              </SidebarIcon>
            </DarkTooltip>
          </Stack>
          <Stack
            sx={{ cursor: 'pointer' }}
            spacing={2}
            alignItems="center"
            paddingBottom={7.5}
            onClick={handleProfileClick}
            onMouseEnter={() => setIsProfileHovered(true)}
            onMouseLeave={() => setIsProfileHovered(false)}
          >
            <DarkTooltip title={collapsed ? 'Profile' : ''}>
              <Avatar
                sx={{
                  backgroundColor: isProfileHovered
                    ? theme.palette.button.dark
                    : theme.palette.primary.main,
                  fontSize: '12px',
                  width: '24px',
                  height: '24px',
                }}
              >
                {getInitial()}
              </Avatar>
            </DarkTooltip>
          </Stack>
        </Stack>
        <Stack
          height="100%"
          justifyContent="space-between"
          sx={{
            py: 2,
            transition: 'width 0.3s ease-in-out',
            transitionDelay: collapsed ? '0.1s' : '0',
          }}
          width={collapsed ? '0px' : '150px'}
        >
          <Stack
            spacing={3}
            paddingTop={5}
            sx={{
              opacity: collapsed ? 0 : 1,
              transition: 'opacity 0.2s ease-in-out',
              transitionDelay: collapsed ? '0' : '0.2s',
            }}
          >
            <BrandLogoChat width="108px" height="40px" />
            <Text
              variant="body1"
              sx={{
                fontWeight: 'bold',
                textWrap: 'nowrap',
                color: isNewChatHovered
                  ? theme.palette.primary.main
                  : theme.palette.text.primary,
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.primary.main,
                },
                marginTop: '25px !important',
              }}
              onClick={handleNewChatClick}
              onMouseEnter={() => setIsNewChatHovered(true)}
              onMouseLeave={() => setIsNewChatHovered(false)}
            >
              New Chat
            </Text>
            <Text
              variant="body1"
              sx={{
                fontWeight: 'bold',
                textWrap: 'nowrap',
                color: isReportLibraryHovered
                  ? theme.palette.primary.main
                  : theme.palette.text.primary,
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.primary.main,
                },
                marginTop: '39px !important',
              }}
              onClick={handleRecentReportsClick}
              onMouseEnter={() => setIsReportLibraryHovered(true)}
              onMouseLeave={() => setIsReportLibraryHovered(false)}
            >
              Report Library
            </Text>
          </Stack>
          <Stack
            spacing={3}
            paddingBottom={3.75}
            sx={{
              opacity: collapsed ? 0 : 1,
              transition: 'opacity 0.2s ease-in-out',
              transitionDelay: collapsed ? '0s' : '0.2s',
            }}
          >
            {isLimitedUser && (
              <ReportUsageCard
                reportsRun={reportsRun}
                reportsLimit={reportLimit}
                progressValue={reportsRunPercentage}
                sx={{
                  marginLeft: `-48px !important`,
                  marginRight: '8px !important',
                }}
                onUpgradeClick={handleUpgradePlanClick}
              />
            )}
            {/* Profile Section */}
            <Box
              ref={profileRef}
              onClick={handleProfileClick}
              onMouseEnter={() => setIsProfileHovered(true)}
              onMouseLeave={() => setIsProfileHovered(false)}
            >
              <ProfileSection account={account} isHovered={isProfileHovered} />
            </Box>
          </Stack>
        </Stack>
      </Box>
      <HelpModal open={openHelpModal} onClose={() => setOpenHelpModal(false)} />
      <ProfileModal
        open={openProfileModal}
        onClose={() => setOpenProfileModal(false)}
        onUpgradePlanClick={handleUpgradePlanClick}
        onHelpClick={handleHelpModalClick}
        onFeedbackClick={handleFeedbackFormClick}
      />
    </>
  );
};
